import React, { useState, useEffect } from "react";
import { Form, Input, Button, Space, Select } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import {
  LargeVolumeDeliveryZone,
  LargeVolumeDeliveryPostalCode,
  CreateLargeVolumeDeliveryPostalCodeRequest,
  UpdateLargeVolumeDeliveryPostalCodeRequest,
} from "./types";

type PostalCodeFormProps = {
  restaurantId: string;
  zones: LargeVolumeDeliveryZone[];
  onSuccess: () => void;
  editingPostalCode?: LargeVolumeDeliveryPostalCode | null;
  onCancelEdit?: () => void;
};

export const PostalCodeForm: React.FC<PostalCodeFormProps> = ({
  restaurantId,
  zones,
  onSuccess,
  editingPostalCode,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const { createSuccess, updateSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingPostalCode) {
      form.setFieldsValue({
        postal_code: editingPostalCode.postal_code,
        zone_id: editingPostalCode.zone_id,
      });
    } else {
      form.resetFields();
    }
  }, [editingPostalCode, form]);

  const onFinish = (values: { postal_code: string; zone_id: string }) => {
    setLoading(true);

    if (editingPostalCode) {
      const request: UpdateLargeVolumeDeliveryPostalCodeRequest = {
        zone_id: values.zone_id,
      };

      fetchT<{ postal_code: string }>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_postal_codes/${editingPostalCode.postal_code}`,
        request,
        () => {
          updateSuccess(t("postal_code"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_update_postal_code"), err);
          setLoading(false);
        }
      );
    } else {
      const request: CreateLargeVolumeDeliveryPostalCodeRequest = {
        postal_code: values.postal_code,
        zone_id: values.zone_id,
      };

      fetchT<{ postal_code: string }>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_postal_codes`,
        request,
        () => {
          createSuccess(t("postal_code"));
          form.resetFields();
          onSuccess();
          setLoading(false);
        },
        (err) => {
          notifyError(t("failed_to_create_postal_code"), err);
          setLoading(false);
        }
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        name="postal_code"
        label={t("postal_code")}
        rules={[{ required: true, message: t("postal_code_is_required") }]}
      >
        <Input placeholder={t("enter_postal_code")} disabled={!!editingPostalCode} />
      </Form.Item>

      <Form.Item
        name="zone_id"
        label={t("delivery_zone")}
        rules={[{ required: true, message: t("delivery_zone_is_required") }]}
      >
        <Select placeholder={t("select_delivery_zone")}>
          {zones.map((zone) => (
            <Select.Option key={zone.id} value={zone.id}>
              {zone.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {editingPostalCode ? t("save") : t("create")}
          </Button>
          <Button onClick={handleCancel}>{editingPostalCode ? t("cancel") : t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
